import React, { useMemo, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { nanoid } from 'nanoid';
import { Box, Flex, OutlineButton, Text } from '@cko/primitives';
import { IconActionUpload } from '@cko/icons';
import { useDashboardToast } from '../../context';
import { FileErrorStatus, FileStatus } from './types';
import { formatBytes, getAcceptsDescription, getErrorMap, getValidFilesCount } from './utils';
import { DroppedFilesSummary } from './DroppedFilesSummary';
const StyledFileUploader = styled.div `
  border-radius: var(--border-r-seed);

  border: ${({ isDragActive }) => isDragActive
    ? 'var(--border-b-seed) dashed var(--base-border-primary)'
    : 'var(--border-b-seed) solid var(--base-border-secondary)'};

  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'default')};
`;
export const FileUploader = ({ files, accept = { 'image/jpeg': ['.jpeg', '.jpg'], 'image/png': ['.png'], 'application/pdf': ['.pdf'] }, maxFiles = 0, maxSize = 1024 * 1024 * 4, disabled, children, onDrop, onAsyncError, onDelete, errors, }) => {
    const [droppedFiles, setDroppedFiles] = useState({});
    const { showToast } = useDashboardToast();
    const errorMap = useMemo(() => getErrorMap(maxSize, maxFiles, errors), [maxSize, maxFiles, errors]);
    useEffect(() => {
        setDroppedFiles(files.reduce((acc, file) => ({
            ...acc,
            [file.id]: { status: FileStatus.Idle, name: file.name },
        }), {}));
    }, [files]);
    async function handleDrop(acceptedFiles, fileRejections) {
        if (maxFiles && acceptedFiles.length > maxFiles - getValidFilesCount(droppedFiles)) {
            showToast(errorMap[FileErrorStatus.MaxFilesExceeded], {
                variant: 'alert',
            });
            return;
        }
        acceptedFiles.forEach(async (file) => {
            const key = nanoid(12);
            setDroppedFiles((prev) => ({
                ...prev,
                [key]: { status: FileStatus.Loading, name: file.name },
            }));
            try {
                const id = await onDrop(file);
                setDroppedFiles((prev) => {
                    const updatedDroppedFiles = {
                        ...prev,
                        [id]: { ...prev[key], status: FileStatus.Success },
                    };
                    delete updatedDroppedFiles[key];
                    return updatedDroppedFiles;
                });
            }
            catch (error) {
                setDroppedFiles((prev) => ({
                    ...prev,
                    [key]: {
                        status: FileStatus.Error,
                        errors: [onAsyncError(error)],
                        name: file.name,
                    },
                }));
            }
        });
        fileRejections.forEach((rejection) => {
            setDroppedFiles((prev) => ({
                ...prev,
                [rejection.file.name]: {
                    status: FileStatus.Error,
                    errors: rejection.errors.map((error) => errorMap[error.code] ?? error.message),
                    name: rejection.file.name,
                },
            }));
        });
    }
    const handleDelete = (key, isUploaded) => {
        if (isUploaded) {
            onDelete(key);
        }
        setDroppedFiles((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        disabled,
        accept,
        maxSize,
        noClick: true,
        noKeyboard: true,
        multiple: true,
        onDrop: handleDrop,
    });
    return (React.createElement(StyledFileUploader, { isDragActive: isDragActive, isDisabled: disabled, ...getRootProps() },
        React.createElement("input", { ...getInputProps(), "data-testid": "file-input" }),
        React.createElement(DroppedFilesSummary, { files: droppedFiles, handleDelete: handleDelete }, children),
        React.createElement(Flex, { p: "var(--layout-stamp)", alignItems: "center" },
            React.createElement(Box, { mr: "var(--layout-stamp)" },
                React.createElement(OutlineButton, { iconStart: IconActionUpload, onClick: open, type: "button", disabled: disabled }, "Upload file")),
            React.createElement(Text, null, `Drag and drop files here to upload. ${getAcceptsDescription(accept)}, and maximum ${formatBytes(maxSize)} in size.`))));
};
